import { getWindowOrNull } from '@/lib/browser/runtime';

const DEVICE_ID_STORAGE_KEY = 'chattenger:web:device-id';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value: string) => UUID_PATTERN.test(value);

const createFallbackUuid = () =>
    '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (character) => {
        const nextByte =
            typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
                ? crypto.getRandomValues(new Uint8Array(1))[0]
                : Math.floor(Math.random() * 256);

        return (Number(character) ^ (nextByte & (15 >> (Number(character) / 4)))).toString(16);
    });

const createDeviceId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return createFallbackUuid();
};

const getSafeLocalStorage = (browserWindow: Pick<Window, 'localStorage'>) => {
    try {
        return browserWindow.localStorage ?? null;
    } catch {
        return null;
    }
};

export const getOrCreateBrowserDeviceId = ({
    browserWindow = getWindowOrNull(),
}: {
    browserWindow?: Pick<Window, 'localStorage'> | null;
} = {}) => {
    if (!browserWindow) {
        return '00000000-0000-4000-8000-000000000000';
    }

    const localStorage = getSafeLocalStorage(browserWindow);

    if (!localStorage) {
        return createDeviceId();
    }

    try {
        const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);

        if (existing && isUuid(existing)) {
            return existing;
        }
    } catch {
        return createDeviceId();
    }

    const nextDeviceId = createDeviceId();

    try {
        localStorage.setItem(DEVICE_ID_STORAGE_KEY, nextDeviceId);
    } catch {
        return nextDeviceId;
    }

    return nextDeviceId;
};
